import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-experte";
const TITLE = "Shopify Experte finden: Auswahl, Kosten, Prüffragen";
const DESCRIPTION =
  "Woran Sie einen echten Shopify Experten erkennen: Nachweise, Preismodelle, Prüffragen vor der Beauftragung, und wann sich ein Freelancer statt eines Teams lohnt.";

const faqs = [
  {
    q: "Was macht ein Shopify Experte?",
    a: "Ein Shopify Experte richtet Store, Theme, Checkout und Tracking so ein, dass aus Besuchern Käufer werden. Der Schwerpunkt liegt je nach Person auf Design, Entwicklung oder Conversion-Optimierung, die drei sollten nicht verwechselt werden.",
  },
  {
    q: "Was kostet ein Shopify Experte?",
    a: "Übliche Stundensätze liegen bei 80 bis 150 € netto. Feste Projektpakete beginnen bei etwa 3.990 € netto für einen vierwöchigen Conversion-Sprint und reichen bis in den fünfstelligen Bereich für laufende Betreuung.",
  },
  {
    q: "Woran erkenne ich einen guten Shopify Experten?",
    a: "An belegbaren Vorher-Nachher-Kennzahlen, an einer klaren Reihenfolge der Maßnahmen und daran, dass er vor dem Angebot sagt, wenn sich ein Projekt nicht rechnet. Ein Portfolio mit Screenshots ohne Zahlen ist kein Nachweis.",
  },
  {
    q: "Freelancer oder Team?",
    a: "Bis etwa 250.000 € Monatsumsatz reicht in der Regel ein Freelancer mit klarer Spezialisierung. Darüber lohnt sich ein Team, sobald parallel Design, Entwicklung, Content und Ads laufen müssen.",
  },
  {
    q: "Wie lange dauert ein Projekt mit einem Shopify Experten?",
    a: "Ein fokussierter Conversion-Sprint dauert vier Wochen: Audit, Umsetzung, Test. Ein kompletter Store-Neubau liegt je nach Sortiment bei sechs bis zwölf Wochen.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-experte")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Auswahl"
      title="Shopify Experte: wen Sie brauchen und woran Sie ihn erkennen"
      intro="„Shopify Experte“ ist keine geschützte Bezeichnung. Diese Übersicht zeigt die drei Profile hinter dem Begriff, die realistischen Preise und die Fragen, die vor der Beauftragung Klarheit schaffen."
      source="ratgeber-shopify-experte"
      sections={[
        {
          h: "Drei Profile, die alle „Experte“ heißen",
          p: [
            "Der Unterschied entscheidet über das Ergebnis. Wer ein Umsatzproblem hat und einen Designer beauftragt, bekommt einen schöneren Shop mit derselben Kaufabschlussrate.",
          ],
          list: [
            "Designer: Erscheinungsbild, Marke, Layout, wirkt selten direkt auf die Conversion",
            "Entwickler: Theme-Code, Apps, Schnittstellen, Performance",
            "Conversion-Spezialist: Diagnose über Zahlen, Eingriffe entlang des Kauftrichters",
          ],
        },
        {
          h: "Was ein Shopify Experte kostet",
          p: [
            "Stundensätze liegen im DACH-Raum bei 80 bis 150 € netto. Sinnvoller als Stunden ist ein fester Umfang: Sie kennen das Budget am ersten Tag, und niemand verdient daran, langsam zu arbeiten.",
            "Meine Pakete liegen bei 3.990 € (vier Wochen), 9.900 € (drei Monate) und 18.900 € (sechs Monate) netto.",
          ],
        },
        {
          h: "Fünf Prüffragen vor der Beauftragung",
          p: [],
          list: [
            "Welche Kennzahl soll sich verändern, und wie wird sie gemessen?",
            "Welche zwei Hebel gehen Sie zuerst an und warum diese?",
            "Zeigen Sie mir eine Vorher-Nachher-Zahl aus einem vergleichbaren Shop.",
            "Was passiert, wenn das Ziel nach der Laufzeit nicht erreicht ist?",
            "Wer arbeitet tatsächlich im Store, Sie oder jemand anderes?",
          ],
        },
        {
          h: "Warnzeichen",
          p: [
            "Angebote ohne definierte Kennzahl, Maßnahmenlisten mit 40 Punkten ohne Reihenfolge, Erfolgsgarantien auf Umsatzsteigerung und monatliche Verträge ohne Kündigungsfrist gehören zu den verlässlichen Warnzeichen.",
          ],
        },
        {
          h: "Wann sich die Investition rechnet",
          p: [
            "Das hängt von Traffic, Conversion-Rate, Warenkorb, Marge und Aufwand ab. Ein Rechenbeispiel: 25.000 Sessions, 70 € Warenkorb und eine angenommene Verbesserung von 1,4 auf 2,0 Prozent ergäben rund 10.500 € zusätzlichen Umsatz pro Monat. Das ist eine Annahme, keine Prognose, und Umsatz ist nicht Gewinn.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-agentur-kosten", label: "Agentur vs. Freelancer" },
        { to: "/shopify-freelancer", label: "Shopify Freelancer" },
        { to: "/roi-rechner", label: "Conversion-Rechner" },
      ]}
    />
  );
}
